import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Box, Button, TextField, Typography, CircularProgress, Stack } from '@mui/material';

const SCRIPT_URL = 'https://verify.msg91.com/otp-provider.js';

/** Same widget as Business Mitra (`register_constants.dart`) — env vars override these. */
const DEFAULT_MSG91_WIDGET_ID = '36686a664333323230373237';
const DEFAULT_MSG91_WIDGET_TOKEN_AUTH = '453798TM6HdgCu6e6a7973daP1';

const WIDGET_ID =
  String(process.env.REACT_APP_MSG91_OTP_WIDGET_ID || '').trim() ||
  DEFAULT_MSG91_WIDGET_ID;
const WIDGET_TOKEN_AUTH =
  String(process.env.REACT_APP_MSG91_OTP_WIDGET_TOKEN_AUTH || '').trim() ||
  DEFAULT_MSG91_WIDGET_TOKEN_AUTH;

/**
 * MSG91 `retryOtp` requires an explicit channel for many widgets (null is rejected).
 * SMS: 11, Voice: 4, Email: 3, WhatsApp: 12 — override with REACT_APP_MSG91_OTP_RESEND_CHANNEL.
 * Set to the literal `null` in env to pass null (default-widget mode only).
 */
const rawResendChannel = process.env.REACT_APP_MSG91_OTP_RESEND_CHANNEL;
const RETRY_OTP_CHANNEL =
  rawResendChannel === undefined || String(rawResendChannel).trim() === ''
    ? '11'
    : String(rawResendChannel).trim().toLowerCase() === 'null'
      ? null
      : String(rawResendChannel).trim();

/** Industry-standard resend cooldown (seconds). */
const RESEND_COOLDOWN_SECONDS = Math.max(
  15,
  Math.min(
    120,
    Number.parseInt(
      String(process.env.REACT_APP_MSG91_OTP_RESEND_COOLDOWN_SECONDS || '30'),
      10
    ) || 30
  )
);

export const isMsg91WidgetConfigured = () =>
  Boolean(String(WIDGET_ID).trim() && String(WIDGET_TOKEN_AUTH).trim());

/**
 * OTP session id from sendOtp / verifyOtp — same value as MSG91 `reqId` (24-char id).
 * Backend verifyAccessToken expects `requestUserId`; browser verifyOtp **4th argument is reqId**, not widgetId.
 */
function walkFindMsg91SessionId(obj, depth = 0) {
  if (depth > 14 || obj == null) return '';
  if (typeof obj === 'string') {
    const s = obj.trim();
    return /^[a-z0-9]{24}$/i.test(s) ? s : '';
  }
  if (typeof obj !== 'object') return '';
  for (const v of Object.values(obj)) {
    const f = walkFindMsg91SessionId(v, depth + 1);
    if (f) return f;
  }
  return '';
}

function extractMsg91SessionMeta(data) {
  if (data == null || typeof data !== 'object') return {};
  const pick = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    const keys = [
      'requestUserId',
      'request_user_id',
      'reqId',
      'req_id',
      'requestId',
      'request_id',
    ];
    for (const k of keys) {
      const v = obj[k];
      if (v != null && String(v).trim()) return String(v).trim();
    }
    // MSG91 sendOTP often returns the session id in `message`
    const msg = obj.message != null ? String(obj.message).trim() : '';
    if (/^[a-z0-9]{24}$/i.test(msg)) return msg;
    return '';
  };
  let id = pick(data);
  if (!id) id = pick(data.data);
  if (!id) id = pick(data.result);
  if (!id) id = pick(data.response);
  if (!id) id = walkFindMsg91SessionId(data);
  return id ? { requestUserId: id, reqId: id } : {};
}

function formatSdkError(err, fallback) {
  if (err == null) return fallback;
  if (typeof err === 'string') return err.trim() || fallback;
  if (typeof err === 'object') {
    const m =
      err.message ||
      err.error ||
      err.msg ||
      (typeof err.description === 'string' ? err.description : null);
    if (m) return String(m);
    try {
      const s = JSON.stringify(err);
      if (s && s !== '{}') return s;
    } catch {
      /* ignore */
    }
  }
  return fallback;
}

/**
 * MSG91 OTP widget (exposeMethods): sendOtp → user enters OTP → verifyOtp → access token for backend.
 * SMS is sent by MSG91 from the browser widget — not via Locsy backend SMS.
 */
const Msg91OtpWidget = ({
  /** 10-digit Indian mobile without country code */
  phoneDigits10,
  onVerified,
  onError,
  disabled = false,
  /** Label for the verify action (e.g. "Verify OTP & Continue") */
  verifyButtonLabel = 'Verify OTP',
}) => {
  const [scriptReady, setScriptReady] = useState(false);
  const [initDone, setInitDone] = useState(false);
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [cooldownLeft, setCooldownLeft] = useState(0);
  /** MSG91 may fire the global `configuration.success` with the JWT while `verifyOtp` gets a slimmer payload. */
  const accessTokenFromSdkSuccessRef = useRef(null);
  /** requestUserId / reqId from sendOtp / verify callbacks for server verifyAccessToken */
  const sessionMetaRef = useRef({});
  const cooldownTimerRef = useRef(null);

  /** MSG91 docs: some flows put the JWT in `message`; SDK may nest under data/result. */
  const JWT_RE =
    /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9._-]+$/;

  const findJwtStringDeep = useCallback((value, depth = 0) => {
    if (depth > 8 || value == null) return null;
    if (typeof value === 'string') {
      const s = value.trim();
      if (JWT_RE.test(s) && s.length >= 20) return s;
      return null;
    }
    if (typeof value !== 'object') return null;
    if (Array.isArray(value)) {
      for (const item of value) {
        const f = findJwtStringDeep(item, depth + 1);
        if (f) return f;
      }
      return null;
    }
    for (const v of Object.values(value)) {
      const f = findJwtStringDeep(v, depth + 1);
      if (f) return f;
    }
    return null;
  }, []);

  const extractAccessToken = useCallback(
    (data) => {
      if (data == null) return null;
      if (typeof data === 'string') {
        const s = data.trim();
        if (JWT_RE.test(s)) return s;
        return null;
      }
      if (typeof data !== 'object') return null;

      const pickString = (obj, keys) => {
        for (const k of keys) {
          const v = obj[k];
          if (typeof v === 'string' && v.trim()) return v.trim();
        }
        return null;
      };

      const keys = [
        'access-token',
        'access_token',
        'accessToken',
        'token',
        'jwt',
        'jwtToken',
        'accessTokenJwt',
        'message',
      ];
      let t = pickString(data, keys);
      if (t && JWT_RE.test(t)) return t;

      const nested = data.data ?? data.result ?? data.response ?? data.payload ?? data.body;
      if (nested && typeof nested === 'object') {
        t = pickString(nested, keys);
        if (t && JWT_RE.test(t)) return t;
      }

      t = findJwtStringDeep(data);
      return t;
    },
    [findJwtStringDeep]
  );

  const clearCooldown = useCallback(() => {
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    setCooldownLeft(0);
  }, []);

  const startCooldown = useCallback(() => {
    clearCooldown();
    setCooldownLeft(RESEND_COOLDOWN_SECONDS);
    cooldownTimerRef.current = setInterval(() => {
      setCooldownLeft((prev) => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearCooldown]);

  useEffect(() => {
    if (!isMsg91WidgetConfigured()) return undefined;

    let cancelled = false;

    const load = () =>
      new Promise((resolve, reject) => {
        if (window.initSendOTP && window.sendOtp) return resolve();
        const existing = document.getElementById('msg91-otp-provider-script');
        if (existing) {
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }
        const s = document.createElement('script');
        s.id = 'msg91-otp-provider-script';
        s.src = SCRIPT_URL;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = reject;
        document.body.appendChild(s);
      });

    (async () => {
      try {
        await load();
        if (cancelled) return;
        setScriptReady(true);
      } catch (e) {
        if (!cancelled) {
          onError?.(e?.message || 'Failed to load phone verification');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [onError]);

  useEffect(() => {
    if (!scriptReady || !window.initSendOTP || initDone) return;

    const configuration = {
      widgetId: WIDGET_ID,
      tokenAuth: WIDGET_TOKEN_AUTH,
      exposeMethods: true,
      captchaRenderId: '',
      success: (data) => {
        const tok = extractAccessToken(data);
        if (tok) {
          accessTokenFromSdkSuccessRef.current = tok;
        }
        Object.assign(sessionMetaRef.current, extractMsg91SessionMeta(data));
      },
      failure: () => {},
    };

    try {
      const initFn = window.initSendOTP || window.initSendotp;
      if (typeof initFn !== 'function') {
        onError?.('Phone verification could not start');
        return;
      }
      initFn(configuration);
      setInitDone(true);
    } catch (e) {
      onError?.(e?.message || 'Phone verification setup failed');
    }
  }, [scriptReady, initDone, extractAccessToken, onError]);

  // Reset session + UI when phone number changes
  useEffect(() => {
    sessionMetaRef.current = {};
    setSent(false);
    setOtp('');
    setStatusMsg('');
    setSending(false);
    setVerifying(false);
    clearCooldown();
  }, [phoneDigits10, clearCooldown]);

  useEffect(() => () => clearCooldown(), [clearCooldown]);

  const phoneOk = /^[0-9]{10}$/.test(String(phoneDigits10 || ''));

  const refreshSessionFromWidget = () => {
    if (typeof window.getWidgetData !== 'function') return '';
    try {
      const wd = window.getWidgetData();
      Object.assign(sessionMetaRef.current, extractMsg91SessionMeta(wd));
    } catch {
      /* ignore */
    }
    return (
      sessionMetaRef.current.reqId ||
      sessionMetaRef.current.requestUserId ||
      ''
    );
  };

  const getSessionReqId = () =>
    sessionMetaRef.current.reqId ||
    sessionMetaRef.current.requestUserId ||
    refreshSessionFromWidget();

  const markOtpSent = (data, { resent = false } = {}) => {
    Object.assign(sessionMetaRef.current, extractMsg91SessionMeta(data));
    let rid = getSessionReqId();
    if (!rid) rid = refreshSessionFromWidget();
    setSending(false);
    setSent(true);
    setOtp('');
    startCooldown();
    if (!rid) {
      setStatusMsg(
        resent
          ? 'Resend accepted, but session id missing. Try Verify; if it fails, change phone digit and Send OTP again.'
          : 'OTP sent, but session id missing. Enter the code; if Verify fails, tap Send OTP again.'
      );
      return;
    }
    setStatusMsg(
      resent
        ? 'OTP resent. Enter the new code and continue.'
        : 'OTP sent. Enter the code and continue.'
    );
  };

  const handleSendOtp = () => {
    if (!phoneOk || !window.sendOtp) {
      onError?.('Enter a valid 10-digit phone number');
      return;
    }
    if (sending || cooldownLeft > 0) return;
    setSending(true);
    setStatusMsg('');
    sessionMetaRef.current = {};
    try {
      const id = `91${String(phoneDigits10).replace(/\D/g, '').slice(0, 10)}`;
      window.sendOtp(
        id,
        (data) => {
          markOtpSent(data, { resent: false });
        },
        (error) => {
          setSending(false);
          onError?.(formatSdkError(error, 'Failed to send OTP'));
        }
      );
    } catch (e) {
      setSending(false);
      onError?.(formatSdkError(e, 'sendOtp failed'));
    }
  };

  const handleVerifyOtp = () => {
    if (!/^\d{4,8}$/.test(String(otp).trim())) {
      onError?.('Enter the OTP you received');
      return;
    }
    if (!window.verifyOtp) {
      onError?.('Verification is not ready yet. Try again in a moment.');
      return;
    }
    const otpSessionReqId = getSessionReqId();
    if (!otpSessionReqId) {
      onError?.(
        'OTP session not found. Wait for the timer, then Resend OTP, enter the new code, and Verify.'
      );
      return;
    }
    setVerifying(true);
    setStatusMsg('');
    accessTokenFromSdkSuccessRef.current = null;
    try {
      window.verifyOtp(
        String(otp).trim(),
        (data) => {
          setVerifying(false);
          const finishWithToken = () => {
            let token = extractAccessToken(data);
            if (!token) token = accessTokenFromSdkSuccessRef.current;
            if (token) {
              accessTokenFromSdkSuccessRef.current = null;
              const meta = {
                ...sessionMetaRef.current,
                ...extractMsg91SessionMeta(data),
              };
              clearCooldown();
              setStatusMsg('Phone verified');
              onVerified?.(token, meta);
              return true;
            }
            return false;
          };
          if (finishWithToken()) return;
          // SDK sometimes fires `configuration.success` after this callback; merge on next tick.
          setTimeout(() => {
            if (finishWithToken()) return;
            accessTokenFromSdkSuccessRef.current = null;
            onError?.(
              'Verification succeeded but could not complete sign-up. Please try again.'
            );
          }, 0);
        },
        (error) => {
          setVerifying(false);
          accessTokenFromSdkSuccessRef.current = null;
          onError?.(formatSdkError(error, 'Invalid OTP'));
        },
        otpSessionReqId
      );
    } catch (e) {
      setVerifying(false);
      onError?.(formatSdkError(e, 'verifyOtp failed'));
    }
  };

  /**
   * Industry practice: after cooldown, resend via MSG91 `retryOtp` (same session).
   * If retry fails (missing reqId / channel), fall back to a fresh `sendOtp`.
   */
  const handleResendOtp = () => {
    if (!initDone || sending || cooldownLeft > 0 || disabled) return;
    if (!phoneOk) {
      onError?.('Enter a valid 10-digit phone number');
      return;
    }

    const rid = getSessionReqId();
    const fallbackToSend = () => {
      if (!window.sendOtp) {
        setSending(false);
        onError?.('Could not resend OTP. Reload the page and try again.');
        return;
      }
      setSending(true);
      setStatusMsg('');
      try {
        const id = `91${String(phoneDigits10).replace(/\D/g, '').slice(0, 10)}`;
        window.sendOtp(
          id,
          (data) => {
            markOtpSent(data, { resent: true });
          },
          (error) => {
            setSending(false);
            onError?.(formatSdkError(error, 'Could not resend OTP'));
          }
        );
      } catch (e) {
        setSending(false);
        onError?.(formatSdkError(e, 'Could not resend OTP'));
      }
    };

    setSending(true);
    setStatusMsg('');

    if (!rid || typeof window.retryOtp !== 'function') {
      fallbackToSend();
      return;
    }

    try {
      window.retryOtp(
        RETRY_OTP_CHANNEL,
        (data) => {
          markOtpSent(data, { resent: true });
        },
        (err) => {
          // Retry often fails after expiry / bad channel — fall back to fresh send.
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn(
              '[Msg91OtpWidget] retryOtp failed, falling back to sendOtp:',
              formatSdkError(err, 'retry failed')
            );
          }
          fallbackToSend();
        },
        rid
      );
    } catch (e) {
      fallbackToSend();
    }
  };

  if (!isMsg91WidgetConfigured()) {
    return null;
  }

  const resendDisabled =
    disabled || !sent || !initDone || sending || verifying || cooldownLeft > 0;

  return (
    <Box
      sx={{
        py: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        maxWidth: 520,
        mx: 'auto',
      }}
    >
      {!initDone && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
          Loading MSG91 verification…
        </Typography>
      )}

      {!sent ? (
        <Button
          type="button"
          variant="contained"
          disabled={disabled || !phoneOk || sending || !initDone}
          onClick={handleSendOtp}
          sx={{ py: 1.25 }}
        >
          {sending ? <CircularProgress size={22} color="inherit" /> : 'Send OTP'}
        </Button>
      ) : (
        <>
          {statusMsg ? (
            <Typography
              variant="body2"
              color={statusMsg.includes('missing') ? 'warning.main' : 'success.main'}
              sx={{ mb: 1 }}
            >
              {statusMsg}
            </Typography>
          ) : null}

          <TextField
            fullWidth
            label="Enter OTP from SMS"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputProps={{ inputMode: 'numeric', maxLength: 8, autoComplete: 'one-time-code' }}
            disabled={disabled}
            helperText="Code is sent by MSG91 SMS — then verify to continue"
          />

          <Button
            type="button"
            variant="contained"
            fullWidth
            disabled={disabled || verifying || !otp.trim()}
            onClick={handleVerifyOtp}
            sx={{ mt: 1.5, py: 1.25 }}
          >
            {verifying ? <CircularProgress size={22} color="inherit" /> : verifyButtonLabel}
          </Button>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{ mt: 1.5, minHeight: 36 }}
          >
            <Typography variant="body2" color="text.secondary">
              Didn&apos;t receive OTP?
            </Typography>
            {cooldownLeft > 0 ? (
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Resend in {cooldownLeft}s
              </Typography>
            ) : (
              <Button
                type="button"
                variant="text"
                size="small"
                onClick={handleResendOtp}
                disabled={resendDisabled}
                sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0.75 }}
              >
                {sending ? <CircularProgress size={16} /> : 'Resend OTP'}
              </Button>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default Msg91OtpWidget;
